import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Logo } from '../components/Logo';
import { CheckCircle2, Loader2, Mail, ArrowRight, FileText, UserCheck, Stamp, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function DvrGrazie() {
    const [params] = useSearchParams();
    const sessionId = params.get('session_id');
    const draftId = params.get('draft_id');
    const [status, setStatus] = useState('checking'); // checking | paid | error
    const [draft, setDraft] = useState(null);

    useEffect(() => {
        if (!sessionId || !draftId) {
            setStatus('error');
            return;
        }
        let attempts = 0;
        const maxAttempts = 10;
        const interval = setInterval(async () => {
            attempts += 1;
            try {
                // Poll payment status (will trigger _process_public_dvr_paid if not yet processed)
                await axios.get(`${BACKEND_URL}/api/payments/checkout/status/${sessionId}`).catch(() => {});
                const r = await axios.get(`${BACKEND_URL}/api/public/dvr/draft/${draftId}`);
                setDraft(r.data);
                if (r.data.stato === 'converted') {
                    setStatus('paid');
                    clearInterval(interval);
                }
            } catch (e) {
                // keep polling
            }
            if (attempts >= maxAttempts) {
                clearInterval(interval);
                if (status !== 'paid') setStatus('paid'); // optimistic fallback (webhook may still process)
            }
        }, 2000);
        return () => clearInterval(interval);
    }, [sessionId, draftId, status]);

    return (
        <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="dvr-grazie-page">
            <nav className="border-b border-[hsl(var(--border))] py-4">
                <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
                    <Logo />
                    <Link to="/login" className="text-sm font-medium text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--accent))]" data-testid="dvr-grazie-login">Accedi al tuo account</Link>
                </div>
            </nav>

            <section className="py-12 md:py-20 max-w-3xl mx-auto px-4">
                {status === 'checking' && (
                    <Card className="p-10 text-center">
                        <Loader2 className="w-12 h-12 mx-auto animate-spin text-[hsl(var(--accent))] mb-4" />
                        <h1 className="font-display text-2xl mb-2">Stiamo confermando il pagamento...</h1>
                        <p className="text-[hsl(var(--muted-foreground))]">Attendi qualche secondo, ci colleghiamo a Stripe per verificare la transazione.</p>
                    </Card>
                )}

                {(status === 'paid' || (status === 'checking' && draft?.stato === 'converted')) && (
                    <Card className="p-8 md:p-12" data-testid="dvr-grazie-success">
                        <div className="text-center mb-8">
                            <div className="w-20 h-20 mx-auto rounded-full bg-[hsl(145_45%_94%)] flex items-center justify-center mb-4">
                                <CheckCircle2 className="w-12 h-12 text-[hsl(145_55%_34%)]" />
                            </div>
                            <Badge className="bg-[hsl(145_45%_94%)] text-[hsl(145_55%_34%)] font-bold mb-3">PAGAMENTO RICEVUTO</Badge>
                            <h1 className="font-display text-3xl md:text-4xl mb-3">Grazie, la pratica è in lavorazione!</h1>
                            <p className="text-[hsl(var(--muted-foreground))] max-w-xl mx-auto">Abbiamo ricevuto il tuo pagamento. La bozza AI è pronta e un professionista certificato la sta già revisionando.</p>
                        </div>

                        <div className="space-y-3 mb-8">
                            <div className="flex items-start gap-3 p-3 rounded-lg bg-[hsl(var(--muted))]/30">
                                <CheckCircle2 className="w-5 h-5 text-[hsl(145_55%_34%)] mt-0.5 flex-shrink-0" />
                                <div>
                                    <div className="font-semibold text-sm">Account creato</div>
                                    <div className="text-xs text-[hsl(var(--muted-foreground))]">Hai ricevuto un'email all'indirizzo {draft?.email} con il link per accedere alla tua area riservata.</div>
                                </div>
                            </div>
                            <div className="flex items-start gap-3 p-3 rounded-lg bg-[hsl(var(--muted))]/30">
                                <UserCheck className="w-5 h-5 text-[hsl(var(--accent))] mt-0.5 flex-shrink-0" />
                                <div>
                                    <div className="font-semibold text-sm">Professionista in carico</div>
                                    <div className="text-xs text-[hsl(var(--muted-foreground))]">Un RSPP/CSP/CSE del nostro network revisiona la bozza, esegue eventuali integrazioni e ti contatta direttamente per chiarimenti.</div>
                                </div>
                            </div>
                            <div className="flex items-start gap-3 p-3 rounded-lg bg-[hsl(45_100%_96%)] border border-[hsl(45_100%_60%)]">
                                <Mail className="w-5 h-5 text-[hsl(35_90%_45%)] mt-0.5 flex-shrink-0" />
                                <div>
                                    <div className="font-semibold text-sm">DVR firmato via email</div>
                                    <div className="text-xs text-[hsl(var(--muted-foreground))]">Riceverai il PDF con timbro e firma digitale entro <strong>{draft?.urgenza === 'urgente' ? '24-48 ore' : draft?.urgenza === 'veloce' ? '2-3 giorni lavorativi' : '5-7 giorni lavorativi'}</strong>. Validità legale completa (D.Lgs 81/2008).</div>
                                </div>
                            </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4 p-4 rounded-lg bg-[hsl(var(--muted))]/30 text-sm mb-6">
                            <div>
                                <div className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Importo pagato</div>
                                <div className="font-display text-2xl tabular-nums">€{Number(draft?.prezzo_totale || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</div>
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Tempo di consegna</div>
                                <div className="font-semibold flex items-center gap-1.5"><Clock className="w-4 h-4" /> {draft?.urgenza === 'urgente' ? '24-48 ore' : draft?.urgenza === 'veloce' ? '2-3 giorni' : '5-7 giorni'}</div>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <Link to="/login" className="flex-1">
                                <Button className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] font-bold gap-2 h-11" data-testid="dvr-grazie-go-login">
                                    Accedi al tuo account <ArrowRight className="w-4 h-4" />
                                </Button>
                            </Link>
                            <Link to="/dvr" className="flex-1">
                                <Button variant="outline" className="w-full gap-2 h-11" data-testid="dvr-grazie-new">
                                    <Stamp className="w-4 h-4" /> Nuova richiesta
                                </Button>
                            </Link>
                        </div>

                        {draft?.firma_richieste_id && (
                            <p className="text-[10px] text-[hsl(var(--muted-foreground))] text-center mt-6">
                                Codice pratica: {draft.firma_richieste_id}<br />
                                Per qualsiasi domanda, rispondi all'email di conferma o scrivici a <a href="mailto:info@cantiereai.com" className="underline">info@cantiereai.com</a>
                            </p>
                        )}
                    </Card>
                )}

                {status === 'error' && (
                    <Card className="p-10 text-center">
                        <h1 className="font-display text-2xl mb-3 text-[hsl(0_72%_52%)]">Qualcosa è andato storto</h1>
                        <p className="text-[hsl(var(--muted-foreground))] mb-4">Non riusciamo a confermare il tuo pagamento. Se hai pagato, controlla la tua email tra qualche minuto.</p>
                        <Link to="/dvr"><Button className="bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)]" data-testid="dvr-grazie-back">Torna alla richiesta</Button></Link>
                    </Card>
                )}
            </section>
        </div>
    );
}
